import {
  Button,
  Collapse,
  CopySVG,
  FlagHollowSVG,
  Toast,
  useModal,
} from "@newsela/angelou";
import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";
import { filter, maxBy } from "lodash";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";

import { VIEW_PAGE_LUNA_AI_DESIGN } from "@/ai/constants/experiments";
import useLessonPlannerVisibility from "@/assignments/useLessonPlannerVisibility";
import globalToasterRef from "@/base/globalToasterRef";
import AILunaTag from "@/components/AIComponents/AILunaTag/AILunaTag";
import useHasFeature from "@/features/useHasFeature";
import useAssignmentPreview from "@/interactiveVideos/AssignmentPreview/assignmentPreview";
import { StandardsModalProvider } from "@/search/Langston/FilterPanel/filters/Standards/StandardsModalProvider";
import { StandardsSearchProvider } from "@/search/Langston/StandardsSearchProvider";
import getUserProperties from "@/users/getUserProperties";
import useUser from "@/users/useUser";
import { isUnsupportedContentForTeacherTools } from "@/utils/generatedContent";
import TT_AND_AUTO_WP_COPY from "@/view/constants/teacherToolsAndAutoGenWritePrompts";
import ReportContentFormModal from "@/view/ReportContentFormModal";
import usePresentModeContext from "@/view/utils/usePresentModeContext";
import useStudentPreviewContext from "@/view/utils/useStudentPreviewContext";
import LessonPlannerTool from "./LessonPlannerTool";
import { LessonPlannerStandardsModal } from "./LessonPlannerTool/LessonPlannerStandards/LessonPlannerStandardsModal";
import {
  actionButtonsCss,
  algorithmOutputCss,
  bodyCss,
  bodyPaddingCss,
  collapseButtonCss,
  disclaimerCss,
  expanderContentCss,
  headerAndIconCss,
  headerCss,
  headingTextCss,
  newTagCss,
  rootCss,
  tabButtonCss,
  tabListCss,
  taglineCss,
} from "./TeacherTools.style";
import useTeacherToolsGet from "./useTeacherToolsGet";

const ButtonWithTrackEvent = withTrackEvent(Button);

const propTypes = {
  content: PropTypes.object,
  contentMetadata: PropTypes.object,
  currentLevel: PropTypes.object,
};

const TeacherTools = ({ content, contentMetadata, currentLevel }) => {
  const { isPresentModeActive } = usePresentModeContext();
  const isAssignmentPreview = useAssignmentPreview();
  const { data: user } = useUser();
  const { isTeacher, isLiteUser } = getUserProperties(user);
  const canViewLessonPlanner = useLessonPlannerVisibility({
    content,
    contentMetadata,
    currentLevel,
  });

  const { isStudentPreviewOn } = useStudentPreviewContext();
  const [selectedTool, setSelectedTool] = useState("main-idea");

  useEffect(() => {
    if (canViewLessonPlanner && selectedTool !== "lesson-planner") {
      setSelectedTool("lesson-planner");
    }
  }, [canViewLessonPlanner]);

  const [reportContentModalProps, setReportContentModalActive, triggerRef] =
    useModal(false);

  const [showLunaAiBranding] = useHasFeature(VIEW_PAGE_LUNA_AI_DESIGN);

  const [learningObjective, setLearningObjective] = useState("");
  const [supportingMaterials, setSupportingMaterials] = useState({
    includeProjectIdea: false,
    includeMultilingualLearners: false,
    includeAssessmentQuestions: false,
  });
  const [allottedTime, setAllottedTime] = useState(60);
  const [selectedStandards, setSelectedStandards] = useState([]);
  const [
    _standardsModalProps,
    setIsStandardsModalActive,
    _standardsModalTriggerRef,
  ] = useModal(false);

  const {
    isHidden: isTeacherToolsCollapsed,
    onClick: setIsTeacherToolsCollapsed,
  } = Collapse.useCollapse({ isHidden: false });

  const ToggleTeacherToolsButton = () => {
    return (
      <ButtonWithTrackEvent
        __cssFor={{ root: collapseButtonCss }}
        actionName={actionNames.hide}
        objectType={objectTypes.quiz}
        ariaProps={{
          "aria-controls": "teacher-tools",
          "aria-expanded": !isTeacherToolsCollapsed,
        }}
        flavor={Button.flavors.incognito}
        onClick={setIsTeacherToolsCollapsed}
        data-testid="teacher-tools-collapsible"
        dataProps={{
          "data-qa-selector": `teacher_tools-${
            isTeacherToolsCollapsed ? "show" : "hide"
          }`,
        }}
        streamProperties={{
          description: `${
            isTeacherToolsCollapsed ? "Show" : "Hide"
          } Teacher Tools`,
        }}
      >
        {isTeacherToolsCollapsed ? "Show" : "Hide"}
      </ButtonWithTrackEvent>
    );
  };

  const levelsForCurrentLanguage = filter(
    content?.data?.article_levels,
    (level) => level.language === currentLevel.language,
  );

  const isVeiled = content?.data?.is_premium && isLiteUser;
  // Load teacher tools for the max article level (in the current language)
  // instead of the current level to save API costs. The only
  // exception to this is key-terms, where the extracted terms should be
  // findable in the text.

  function getAssumedLevel() {
    if (selectedTool === "key-terms" || levelsForCurrentLanguage.length === 1) {
      return currentLevel;
    }

    return maxBy(levelsForCurrentLanguage, "raw_lexile");
  }

  const assumedLevel = getAssumedLevel();

  // No need to process article when the user doesn't have access to the feature.
  const skipAssetGeneration =
    !isTeacher ||
    isVeiled ||
    isUnsupportedContentForTeacherTools({
      articleType: content?.data?.article_type,
      contentType: content?.content_type,
      text: assumedLevel?.text,
      contentMetadata,
    });

  // Skip asset generation for lesson planner, this is handled separately.
  const isLessonPlannerToolSelected = selectedTool === "lesson-planner";
  const {
    data: generatedTextAsset,
    loading: loadingGeneratedTextAsset,
    error: loadError,
  } = useTeacherToolsGet({
    content,
    selectedTool,
    assumedLevel,
    skip: skipAssetGeneration || isLessonPlannerToolSelected,
  });

  const onCopyClick = () => {
    navigator.clipboard.writeText(generatedTextAsset.algorithm_output);
    globalToasterRef.current.displayToast({
      showIcon: true,
      statusColor: Toast.statusColor.success,
      children: "Copied to clipboard",
    });
  };

  const formatGeneratedText = (generatedText) => {
    if (loadingGeneratedTextAsset) {
      return "Loading...";
    }

    if (loadError) {
      return "Unfortunately, we've run into a problem trying to generate this. Please try again later.";
    }

    if (!generatedText && !loadingGeneratedTextAsset) {
      return "Select from the list above to generate a response in seconds! 🌟💫";
    }

    return generatedText;
  };

  if (
    isPresentModeActive ||
    isStudentPreviewOn ||
    isAssignmentPreview ||
    skipAssetGeneration
  ) {
    return null;
  }

  const streamProperties = {
    component_type: "TeacherTools",
    content_id: assumedLevel?.id,
    content_type: "ArticleLevel",
    object_id: generatedTextAsset?.content_id,
    object_type: "GeneratedTextAsset",
  };

  const tabItems = [
    ...(canViewLessonPlanner
      ? [
          {
            selectedToolId: "lesson-planner",
            title: "Lesson planner",
            qaSelector: "teacher_tools_lesson_planner",
          },
        ]
      : []),
    {
      selectedToolId: "main-idea",
      title: "Main idea",
      qaSelector: "teacher_tools_main_idea",
    },
    {
      selectedToolId: "key-terms",
      title: "Key terms",
      qaSelector: "teacher_tools_key_terms",
    },
    {
      selectedToolId: "before-reading-activity",
      title: "Before reading activity",
      qaSelector: "teacher_tools_before_reading_activity",
    },
    {
      selectedToolId: "discussion-questions",
      title: "Discussion questions",
      qaSelector: "teacher_tools_discussion_questions",
    },
  ].filter((item) => Object.keys(item).length > 0);

  const renderHeader = () => {
    return (
      <div css={headerCss(showLunaAiBranding)}>
        <div>
          <div css={headerAndIconCss}>
            <h4 css={headingTextCss(showLunaAiBranding)}>
              Apply to your lesson
            </h4>
            {showLunaAiBranding && <AILunaTag />}
          </div>
          <p css={taglineCss(showLunaAiBranding)}>
            Review AI-generated suggestions for lesson content and inspiration.
          </p>
        </div>
        <ToggleTeacherToolsButton />
      </div>
    );
  };

  const renderTabButtons = () => (
    <div css={tabListCss} role="tablist">
      {tabItems.map(({ title, selectedToolId, qaSelector }) => (
        <ButtonWithTrackEvent
          key={selectedToolId}
          __cssFor={{
            root: tabButtonCss(selectedTool === selectedToolId),
          }}
          flavor={Button.flavors.polarTertiary}
          onClick={() => setSelectedTool(selectedToolId)}
          role="tab"
          aria-selected={selectedTool === selectedToolId}
          dataProps={{ "data-qa-selector": qaSelector }}
        >
          {title}
          {selectedToolId === "lesson-planner" && (
            <span css={newTagCss}>New</span>
          )}
        </ButtonWithTrackEvent>
      ))}
    </div>
  );

  const renderContent = () => {
    if (selectedTool === "lesson-planner") {
      return (
        <LessonPlannerTool
          contentId={content.id}
          currentLevel={currentLevel}
          learningObjective={learningObjective}
          setLearningObjective={setLearningObjective}
          selectedStandards={selectedStandards}
          setSelectedStandards={setSelectedStandards}
          supportingMaterials={supportingMaterials}
          setSupportingMaterials={setSupportingMaterials}
          allottedTime={allottedTime}
          setAllottedTime={setAllottedTime}
          setStandardsModalActive={setIsStandardsModalActive}
        />
      );
    }

    return (
      <>
        <div css={algorithmOutputCss}>
          {formatGeneratedText(generatedTextAsset?.algorithm_output)}
        </div>
        {generatedTextAsset && !loadingGeneratedTextAsset && (
          <div css={bodyPaddingCss}>
            <div css={disclaimerCss}>{TT_AND_AUTO_WP_COPY.DISCLAIMER}</div>
            <div css={actionButtonsCss}>
              <ButtonWithTrackEvent
                icon={{
                  SvgComponent: CopySVG,
                  alignment: Button.iconAlignments.left,
                }}
                flavor={Button.flavors.muted}
                onClick={onCopyClick}
                actionName="Copy"
                objectType={streamProperties.object_type}
                streamProperties={{
                  ...streamProperties,
                  description: "Copy to clipboard",
                }}
                dataProps={{ "data-qa-selector": "teacher_tools_copy_text" }}
              >
                Copy
              </ButtonWithTrackEvent>
              <ButtonWithTrackEvent
                icon={{
                  SvgComponent: FlagHollowSVG,
                  alignment: Button.iconAlignments.left,
                }}
                flavor={Button.flavors.tertiary}
                onClick={() => setReportContentModalActive(true)}
                ref={triggerRef}
                actionName="Flag"
                objectType={streamProperties.object_type}
                streamProperties={{
                  ...streamProperties,
                  description: "Report Content",
                }}
                dataProps={{ "data-qa-selector": "teacher_tools_flag" }}
              >
                Report content
              </ButtonWithTrackEvent>
            </div>
            <ReportContentFormModal
              contentId={generatedTextAsset.content_id}
              reportContentModalProps={reportContentModalProps}
              setReportContentModalActive={setReportContentModalActive}
              reportSource={"TeacherTools"}
            />
          </div>
        )}
      </>
    );
  };

  return (
    <StandardsSearchProvider>
      <StandardsModalProvider>
        <div css={rootCss(isTeacherToolsCollapsed, showLunaAiBranding)}>
          <Collapse
            isHidden={isTeacherToolsCollapsed}
            renderCustomTrigger={renderHeader}
            __cssFor={{
              expanderContent: expanderContentCss,
            }}
          >
            <div css={bodyCss}>
              {renderTabButtons()}
              {renderContent()}
            </div>
          </Collapse>
          <LessonPlannerStandardsModal
            modalProps={_standardsModalProps}
            setStandardsModalActive={setIsStandardsModalActive}
            selectedStandards={selectedStandards}
            setSelectedStandards={setSelectedStandards}
          />
        </div>
      </StandardsModalProvider>
    </StandardsSearchProvider>
  );
};

TeacherTools.propTypes = propTypes;

export default TeacherTools;
